// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { useQuery } from '@apollo/client';
import { TASK_STATUS, formatTime, isPlantStep, utils } from '@seedplanner/shared-utils';
import dayjs from 'dayjs';
import timezonePlugin from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import Decimal from 'decimal.js';
import gql from 'graphql-tag';
// biome-ignore lint/style/useImportType: <explanation>
import * as React from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router';
import { ExpandoCard, TitleCard } from '../components/common/card';
import { PageError } from '../components/common/error';
import { Time } from '../components/common/time';
import type { ColumnsType } from '../components/common/view';
import PageView from '../components/common/view/PageView';
import { CropIdLink } from '../components/crop';
import CustomerTable from '../components/order/CustomerTable';
import RecurringTaskIdLink from '../components/recurringTask/RecurringTaskIdLink';
import { TaskOptions } from '../components/task';
import TaskPlantingGroupTable from '../components/task/TaskPlantingGroupTable';
import TaskTable from '../components/task/TaskTable';
import { useUserContext } from '../context/UserContext';
import { Box } from '../lib/base';
import * as routes from '../routes';
import type { TaskGroupQuery, TaskGroupQueryVariables } from '../types/__generated__/graphql';
import { createID, formatRecurringTaskName, formatStateOrStatus, getID, getNumericID, shortenSizeUnit } from '../utils';
import { formatNumber } from '../utils/formatNumber';
import { groupPlantings } from '../utils/groupPlantings';

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

const SINGLE_TASK_GROUP_QUERY = gql`
  query TaskGroup($id: ID!) {
    taskGroup(id: $id) {
      crop {
        id
        crop_name
        sowing_density
        sow_density_unit
      }
      tasks {
        id
        task_name
        task_status
        task_time
        task_completed_time
        task_completed_by_name
        recurring_task_id
        recurring_task {
          id
        }
        notes
        planting {
          id
          order_id
          trays_rounded
          planting_time
          planted_at
        }
        order {
          id
          customer
        }
      }
    }
  }
`;

export default function TaskGroupView() {
  const { taskId } = useParams<{ readonly taskId: string }>();
  const { timezone, language } = useUserContext();
  if (taskId == null) {
    return <PageError />;
  }
  const { data, loading, error, refetch } = useQuery<TaskGroupQuery, TaskGroupQueryVariables>(SINGLE_TASK_GROUP_QUERY, {
    variables: { id: createID.task(taskId) },
  });

  if (error) {
    return <PageError error={error} />;
  }
  if (loading || data == null || data.taskGroup == null) {
    return (
      <PageView
        title="Task"
        name="Task"
        pluralName="Tasks"
        pluralRoute={routes.TASKS}
        loading={loading}
        searchRoute={''}
        bodyColumns={[]}
      />
    );
  }
  if (data.taskGroup.tasks.length === 0) {
    return <PageError error={new Error('No tasks found')} />;
  }
  const tasks = data.taskGroup.tasks;
  const sampleTask = tasks[0];
  // Hacky way to get notes if some are empty and some are not
  const taskNotes = tasks.find((t) => t.notes != null && t.notes !== '')?.notes || '-';

  const isForRecurringTask = sampleTask.recurring_task_id != null;

  const maybeRecurringTaskId = sampleTask.recurring_task_id ?? undefined;
  let recurringTask: React.ReactElement | string = '-';
  if (maybeRecurringTaskId) {
    const recurringTaskExists = sampleTask.recurring_task != null;
    recurringTask = recurringTaskExists ? (
      <RecurringTaskIdLink id={maybeRecurringTaskId} />
    ) : (
      `${formatRecurringTaskName(maybeRecurringTaskId)} (deleted)`
    );
  }

  const numCompleted = tasks.filter((t) => t.task_status === TASK_STATUS.COMPLETED).length;
  const allCompleted = numCompleted === tasks.length;
  const taskGroupStatus = allCompleted
    ? TASK_STATUS.COMPLETED
    : numCompleted === 0
      ? TASK_STATUS.PLANNED
      : `${numCompleted} of ${tasks.length} Completed`;

  const totalTrays = Math.ceil(
    tasks.reduce(
      (acc, t) =>
        utils.addTraysRaw(
          acc,
          t.planting?.trays_rounded !== undefined ? Number.parseFloat(t.planting.trays_rounded) : 0,
        ),
      0,
    ),
  );
  const someTaskCompleted = tasks.some((t) => t.task_status === TASK_STATUS.COMPLETED);
  const completedByNames = new Set(tasks.map((t) => t.task_completed_by_name).filter(utils.notNull));
  const completedTime = Math.max(...tasks.map((t) => t.task_completed_time).filter(utils.notNull));

  const crop = data.taskGroup.crop;

  const isPlantTask = isPlantStep(sampleTask.task_name);
  const seedNeeded =
    isPlantTask && crop && crop.sowing_density ? Decimal.mul(crop.sowing_density, totalTrays).toNumber() : undefined;
  const sowDensityUnit = crop?.sow_density_unit ?? 'unknown unit';

  const columns: ColumnsType = [
    ['Task Name', sampleTask.task_name],
    ['Recurring Task', recurringTask],
    ['Crop', crop ? <CropIdLink key={1} id={getID(crop.id)} name={crop.crop_name} /> : '-'],
    ['Trays', isForRecurringTask ? '-' : formatNumber(totalTrays, language)],
    seedNeeded !== undefined
      ? [
          'Seed Needed',
          `${formatNumber(seedNeeded, language)} ${sowDensityUnit} (${crop?.sowing_density ?? 'unknown'}${shortenSizeUnit(sowDensityUnit)} / tray)`,
        ]
      : undefined,
    ['Task Date', <Time key={1} time={sampleTask.task_time} />],
    ['Orders', isForRecurringTask ? '-' : new Set(tasks.map((t) => t.planting?.order_id).filter(utils.notNull)).size],
    ['Status', formatStateOrStatus(taskGroupStatus)],
    ['Notes', taskNotes],
    ['Completed By', someTaskCompleted ? Array.from(completedByNames).join(', ') : '-'],
    [
      'Completed At',
      someTaskCompleted ? (
        <Time key={1} time={completedTime} options={{ time: true, removeDayOfWeek: true }} dontUseTimezone />
      ) : (
        '-'
      ),
    ],
  ];

  // For each task, if there is an order, add the trays_rounded to the order trays map
  // If there is no order and no planting, then skip
  // If there is a planting, but no order, then add to "Unknown" customer
  // It's practicallly impossible to have a task with an order and no planting- so just skip
  const customerAndTrayData = new Map<
    string, // Order ID
    { readonly orderId?: string; readonly customer: string; readonly trays: Decimal }
  >();
  const unknownOrderId = 'Unknown Order';
  for (const task of data.taskGroup.tasks) {
    if (task.order == null) {
      if (task.planting == null) {
        // No order/customer and no planting/trays, so skip
        continue;
      }
      // No order/customer, but has planting/trays, so add to "Unknown" customer
      const maybeExistingCustomer = customerAndTrayData.get(unknownOrderId);
      if (maybeExistingCustomer === undefined) {
        customerAndTrayData.set(unknownOrderId, {
          orderId: undefined,
          customer: unknownOrderId,
          trays: new Decimal(task.planting.trays_rounded),
        });
      } else {
        customerAndTrayData.set(unknownOrderId, {
          orderId: undefined,
          customer: unknownOrderId,
          trays: maybeExistingCustomer.trays.add(new Decimal(task.planting.trays_rounded)),
        });
      }
      continue;
    }
    // Should never have a task with an order but no planting, so skip
    if (task.planting == null) {
      continue;
    }
    const maybeExistingCustomer = customerAndTrayData.get(task.order.customer);
    const trays = task.planting == null ? new Decimal(0) : new Decimal(task.planting.trays_rounded);
    if (maybeExistingCustomer === undefined) {
      customerAndTrayData.set(task.order.id, {
        orderId: task.order.id,
        customer: task.order.customer,
        trays,
      });
    } else {
      customerAndTrayData.set(task.order.id, {
        orderId: task.order.id,
        customer: task.order.customer,
        trays: maybeExistingCustomer.trays.add(trays),
      });
    }
  }
  const orderCustomerData = Array.from(customerAndTrayData.values());

  const orderCustomerTable = isForRecurringTask ? null : (
    <Box sx={{ mt: 2 }}>
      <TitleCard
        title="Affected Orders"
        rightElement={<div />}
        content={<CustomerTable orders={orderCustomerData} />}
      />
    </Box>
  );

  const plantings = tasks.map((t) => t.planting).filter(utils.notNull);
  const groupedPlantings = groupPlantings(plantings);
  const plantingGroupsTable = (
    <Box sx={{ mt: 2 }}>
      <TitleCard
        title="Plantings"
        rightElement={<div />}
        content={<TaskPlantingGroupTable plantings={groupedPlantings} />}
      />
    </Box>
  );

  const subTasksTable = (
    <Box sx={{ mt: 2 }}>
      <ExpandoCard
        title={isForRecurringTask ? 'Individual Tasks' : 'Planting-Specific Tasks'}
        rightElement={<div />}
        content={<TaskTable useIndividualTaskLinks tasks={tasks} />}
      />
    </Box>
  );
  const extra = (
    <>
      {orderCustomerTable}
      {plantingGroupsTable}
      {subTasksTable}
    </>
  );

  // Can move one day forward if- task is not completed, if task date is not same day as planting date
  const plantingDate = sampleTask.planting ? dayjs.unix(sampleTask.planting.planting_time).tz(timezone) : null;
  const taskDate = dayjs.unix(sampleTask.task_time).tz(timezone);
  const plantingDateIsFuture = plantingDate?.isAfter(taskDate, 'day') ?? false;
  const canEditTaskDateOneDay = plantingDateIsFuture && !someTaskCompleted;
  const taskGroupOptions = (
    <TaskOptions
      taskIds={tasks.map((t) => getNumericID(t.id))}
      taskStatus={taskGroupStatus}
      taskName={sampleTask.task_name}
      taskTime={sampleTask.task_time}
      canEditTaskDateOneDay={canEditTaskDateOneDay}
      onSuccess={(shouldRefetchSelf) => {
        if (shouldRefetchSelf) {
          void refetch();
        }
      }}
    />
  );

  return (
    <>
      <Helmet>
        <title>{`${sampleTask.task_name} - ${formatTime(sampleTask.task_time, { timezone })} - Task Group`}</title>
      </Helmet>
      <PageView
        title="Task"
        name="Task"
        pluralName="Tasks"
        pluralRoute={routes.TASKS}
        searchRoute={routes.makeTaskGroupIdPath(getID(sampleTask.id))}
        bodyColumns={columns}
        headerRightElement={taskGroupOptions}
        extra={extra}
        loading={loading}
      />
    </>
  );
}
